import axios from 'axios';
import { useFeatureFlags } from '@/composables/useFeatureFlags';

/**
 * The "Also sign out of connected apps?" offer on HOGC One's own sign-out (ADR-0014 R3).
 *
 * Offered, never silent — in either direction. A member signing out of HOGC One on their
 * phone probably does not want their laptop's ACTS session killed, so nothing cascades
 * without asking; and a member walking away from a shared device deserves the question,
 * because the approval lived in this app, so the revocation should be reachable from it.
 *
 * Never blocks the sign-out. The two halves fail differently on purpose:
 * - BEFORE the member is asked (flag off, no token, the list not loading): resolve
 *   quietly. The member never knew an offer existed, and holding their exit hostage to a
 *   listing call would be worse than not asking.
 * - AFTER they opt in: a failed revoke-all gets one alert. They are about to lose the
 *   screen that could have told them, and a security action failing silently is the
 *   exact outcome this feature exists to prevent.
 */
export async function offerConnectedAppsSignOut({
  token,
  // Injected so tests do not have to own the globals; window.confirm is the established
  // pattern for this feature (see connected-apps.vue confirmEndAll).
  confirm = message => window.confirm(message),
  alert = message => window.alert(message),
} = {}) {
  let live;
  try {
    const { isEnabled } = useFeatureFlags();
    if (!isEnabled('sso-broker')) return;
    if (!token) return;

    // Short timeout on the read: this sits inside sign-out, and a slow list is not
    // worth holding the member's exit for.
    const { data } = await axios.get(`${process.env.VUE_APP_API_URL}/v2/sso/sessions`, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 8000,
    });

    // Only live sessions are worth the question. Ones already signing out (or waiting
    // on a retry) are being handled and read confusingly inside a yes/no prompt.
    live = Array.isArray(data) ? data.filter(session => session.status === 'active') : [];
  } catch {
    return;
  }
  if (live.length === 0) return;

  const names = live.map(session => session.appName || session.clientId).join(', ');
  const wantsCascade = confirm(
    `You're still signed in to: ${names}.\n\n` +
      'Also sign out of your connected apps? Choosing Cancel signs you out of HOGC One only.',
  );
  if (!wantsCascade) return;

  try {
    await axios.delete(`${process.env.VUE_APP_API_URL}/v2/sso/sessions`, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 20000,
    });
  } catch {
    alert('Some apps could not be signed out. You can retry from Me → Connected apps.');
  }
}
